import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

/**
 * Fetches active ads for a given placement.
 * Returns [] while loading or on error — pages gracefully show nothing.
 */
export function useAds(placement) {
  const [ads, setAds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/ads`, { params: { placement } })
      .then(r => setAds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAds([]));
  }, [placement]);

  return ads;
}

/**
 * Inject ads into an items array, maintaining the original total count.
 *
 * @param {Array}  items   - content items (routes, airports, airlines, posts…)
 * @param {Array}  ads     - active ads from useAds()
 * @param {number} slots   - how many ad slots to fill (default 1, max 3).
 *                           If fewer ads exist than slots, the available ads
 *                           are cycled (same ad can appear multiple times).
 *
 * Grid integrity: trims `slots` items from the end before injecting,
 * so total = original length regardless of how many ads are shown.
 * Ads are distributed evenly when slots > 1; admin-configured `position`
 * is honoured only for single-slot mode.
 */
export function injectAds(items, ads, slots = 1) {
  if (!ads.length) return items;

  const count = Math.min(slots, 3); // hard cap at 3 ads per page

  // Trim so total stays the same
  const trimmed = items.slice(0, Math.max(0, items.length - count));
  const result = [...trimmed];

  if (count === 1) {
    // Single slot: respect the admin-configured position
    const pos = Math.min(ads[0].position || 4, result.length);
    result.splice(pos, 0, { __isAd: true, ...ads[0] });
  } else {
    // Multiple slots: distribute evenly through the list
    const spacing = Math.floor(result.length / (count + 1));
    const insertions = Array.from({ length: count }, (_, i) => ({
      pos: spacing * (i + 1),
      ad: ads[i % ads.length], // cycle through available ads
    }));
    // Insert from end → front so earlier positions don't shift
    insertions
      .sort((a, b) => b.pos - a.pos)
      .forEach(({ pos, ad }) =>
        result.splice(Math.min(pos, result.length), 0, { __isAd: true, ...ad })
      );
  }

  return result;
}
